import apiClient from './api';
import {
  GreetingItem,
  GreetingListResponse,
  CreateGreetingRequest,
  UpdateGreetingRequest,
  GreetingFilterOptions
} from '../types/greeting';

class GreetingService {
  // 레이아웃 목록 조회
  async getLayouts(): Promise<string[]> {
    try {
      const response = await apiClient.get('/greetings/layouts');
      return response.data.data;
    } catch (error) {
      console.error('레이아웃 목록 조회 실패:', error);
      // 기본값 반환
      return [
        '-'
      ];
    }
  }

  // 그리팅 목록 조회
  async getGreetingList(options: GreetingFilterOptions = {}): Promise<GreetingListResponse> {
    try {
      const params = new URLSearchParams();

      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.exposureType && options.exposureType !== 'all') {
        params.append('exposure', options.exposureType);
      }
      if (options.searchKeyword) params.append('search', options.searchKeyword);

      const response = await apiClient.get(`/greetings?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('그리팅 목록 조회 실패:', error);
      throw new Error('그리팅 목록을 불러오는데 실패했습니다.');
    }
  }

  // 그리팅 상세 조회
  async getGreetingDetail(id: number): Promise<GreetingItem> {
    try {
      const response = await apiClient.get(`/greetings/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('그리팅 상세 조회 실패:', error);
      throw new Error('그리팅 상세 정보를 불러오는데 실패했습니다.');
    }
  }

  // 그리팅 생성
  async createGreeting(greetingData: CreateGreetingRequest): Promise<GreetingItem> {
    try {
      const response = await apiClient.post('/greetings', greetingData);
      return response.data.data;
    } catch (error) {
      console.error('그리팅 생성 실패:', error);
      throw new Error('그리팅 생성에 실패했습니다.');
    }
  }

  // 그리팅 수정
  async updateGreeting(greetingData: UpdateGreetingRequest): Promise<GreetingItem> {
    try {
      const { id, ...updateData } = greetingData;
      const response = await apiClient.put(`/greetings/${id}`, updateData);
      return response.data.data;
    } catch (error) {
      console.error('그리팅 수정 실패:', error);
      throw new Error('그리팅 수정에 실패했습니다.');
    }
  }

  // 그리팅 삭제
  async deleteGreeting(id: number): Promise<void> {
    try {
      await apiClient.delete(`/greetings/${id}`);
    } catch (error) {
      console.error('그리팅 삭제 실패:', error);
      throw new Error('그리팅 삭제에 실패했습니다.');
    }
  }

  // 그리팅 일괄 삭제
  async bulkDeleteGreetings(ids: number[]): Promise<{ deletedCount: number; deletedIds: number[] }> {
    try {
      const response = await apiClient.post('/greetings/bulk-delete', { ids });
      return response.data.data;
    } catch (error) {
      console.error('그리팅 일괄 삭제 실패:', error);
      throw new Error('선택한 그리팅 삭제에 실패했습니다.');
    }
  }

  // 그리팅 일괄 노출/비노출 상태 변경
  async bulkToggleExposure(
    ids: number[],
    exposureType: 'show' | 'hide'
  ): Promise<{ updatedCount: number; exposureType: string; updatedIds: number[] }> {
    try {
      const response = await apiClient.patch('/greetings/bulk-exposure', {
        ids,
        exposureType
      });
      return response.data.data;
    } catch (error) {
      console.error('그리팅 일괄 노출 상태 변경 실패:', error);
      throw new Error('선택한 그리팅의 노출 상태 변경에 실패했습니다.');
    }
  }

  // 그리팅 검색
  async searchGreetings(searchKeyword: string, page: number = 1, limit: number = 10): Promise<GreetingListResponse> {
    return this.getGreetingList({
      searchKeyword,
      page,
      limit
    });
  }

  // 노출 상태별 그리팅 조회
  async getGreetingsByExposure(
    exposureType: 'show' | 'hide' | 'all',
    page: number = 1,
    limit: number = 10
  ): Promise<GreetingListResponse> {
    return this.getGreetingList({
      exposureType,
      page,
      limit
    });
  }

  // 그리팅 통계 조회 (추가 기능)
  async getGreetingStats(): Promise<{
    total: number;
    exposed: number;
    hidden: number;
    recentlyCreated: number;
  }> {
    try {
      // 전체 그리팅 조회 (limit을 크게 설정)
      const allGreetings = await this.getGreetingList({ limit: 1000 });
      const greetings = allGreetings.data;

      // 최근 생성 기준 (7일 이내)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const stats = {
        total: greetings.length,
        exposed: greetings.filter(g => g.exposureType === 'show').length,
        hidden: greetings.filter(g => g.exposureType === 'hide').length,
        recentlyCreated: greetings.filter(g =>
          new Date(g.createdAt) > weekAgo
        ).length
      };

      return stats;
    } catch (error) {
      console.error('그리팅 통계 조회 실패:', error);
      return {
        total: 0,
        exposed: 0,
        hidden: 0,
        recentlyCreated: 0
      };
    }
  }
}

export default new GreetingService();